import { Component, OnInit, Input, ViewChild, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { MatSnackBar } from '@angular/material';
import { MatDialog, MatDialogRef } from '@angular/material';

import {
    TdDataTableComponent,
    TdDataTableService,
    TdDataTableSortingOrder,
    ITdDataTableSortChangeEvent,
    ITdDataTableColumn,
    TdDialogService,
    IPageChangeEvent,
} from '@covalent/core';

import { NGXLogger } from 'ngx-logger';

import { TranslateService, LangChangeEvent } from '@ngx-translate/core';

// import { L10nService } from '../l10n/l10n.service';
import { VideoService, AwsS3Service } from '../services';
import { IVideo } from '../models/video.model';
import { IDataArray } from '../models/data-array.model';
import { PreviewDialogComponent } from '../dialogs/preview-dialog/preview-dialog.component';
import { environment } from '../../environments/environment';
import { ProfileService } from '../auth/profile.service';
import { AuthService } from '../auth/auth.service';

@Component({
    selector: 'gk-video-list',
    templateUrl: './video.list.component.html',
    styleUrls: ['./video.list.component.scss'],
})
export class VideoListComponent implements OnInit, OnChanges, OnDestroy {

    private subscription: any;

    private columnsWithEdit: ITdDataTableColumn[] = [
        { name: 'title', label: 'Title', tooltip: 'Video Title' },
        { name: 'thumbnailUrl', label: 'Thumbnail' },
        { name: 'durationSecs', label: 'Duration' },
        { name: 'resolution', label: 'Resolution' },
        { name: 'id', label: '' },
    ];

    private columnsWithoutEdit: ITdDataTableColumn[] = [
        { name: 'title', label: 'Title', tooltip: 'Video Title' },
        { name: 'thumbnailUrl', label: 'Thumbnail' },
        { name: 'durationSecs', label: 'Duration' },
        { name: 'resolution', label: 'Resolution' },
    ];

    @Input()
    isEditable: boolean = true;

    @ViewChild(TdDataTableComponent) dataTable: TdDataTableComponent;

    data: IVideo[] = [];

    lookup: Object = {
        title: 'VideoList.ColHeader.Title',
        thumbnailUrl: 'VideoList.ColHeader.ThumbnailURL',
        durationSecs: 'VideoList.ColHeader.DurationSecs',
        resolution: 'VideoList.ColHeader.Resolution',
    };

    columns: ITdDataTableColumn[] = this.columnsWithEdit;

    filteredData: IVideo[] = this.data;
    filteredTotal: number = this.data.length;

    selectedRows: IVideo[] = [];

    searchTerm: string = '';
    fromRow: number = 1;
    currentPage: number = 1;
    pageSize: number = 50;
    sortBy: string = 'title';
    sortOrder: TdDataTableSortingOrder = TdDataTableSortingOrder.Descending;

    selectedIndexIntoTabGroup: number = 0;

    constructor(private _dataTableService: TdDataTableService,
        private _dialogService: TdDialogService,
        private _videoService: VideoService,
        private _s3: AwsS3Service,
        private _snackBarService: MatSnackBar,
        private _translate: TranslateService,
        private logger: NGXLogger,
        private dialog: MatDialog,
        private auth: AuthService,
    ) { }

    ngOnInit(): void {
        this.subscription = this._translate.onLangChange.subscribe((event: LangChangeEvent) => {
            this.retranslateColumnHeaders();
        });
        this.retranslateColumnHeaders();
        this.filter();
        this.loadVideos();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.isEditable) {
            if (changes.isEditable.currentValue) {
                this.columns = this.columnsWithEdit;
            } else {
                this.columns = this.columnsWithoutEdit;
            }
        }
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    onAccountChangeEvent(event: any): void {
        this.logger.log('video.list: onAccountChangeEvent()');
        this.data = [];
        this.loadVideos();
    }

    onSortEvent(sortEvent: ITdDataTableSortChangeEvent): void {
        this.sortBy = sortEvent.name;
        this.sortOrder = sortEvent.order;
        this.filter();
    }

    onSearchTermEvent(searchTerm: string): void {
        this.searchTerm = searchTerm;
        this.filter();
    }

    onPageChangeEvent(pagingEvent: IPageChangeEvent): void {
        this.fromRow = pagingEvent.fromRow;
        this.currentPage = pagingEvent.page;
        this.pageSize = pagingEvent.pageSize;
        this.filter();
    }

    onPreviewClick(id: string): void {
        let url: string = environment.screenUrl + '/#/videos/' + id + '?' +
            't=' + this.auth.getToken();

        const effectiveAccount: string = ProfileService.profileSingleton().effectiveAccountID;
        if (effectiveAccount) {
            url += '&a=' + effectiveAccount;
        }
        const dialogRef: MatDialogRef<PreviewDialogComponent> = this.dialog.open(PreviewDialogComponent, {
            data: { url: url },
        });
        dialogRef.afterClosed().subscribe();
    }

    onDeleteVideoEvent(id: string): void {

        let videoTitle: string = this.data.find((x: IVideo) => x.id === id).title;

        this._dialogService
            .openConfirm({ title: 'Confirmation Required', message: 'Are you sure you want to delete ' + videoTitle + '?' })
            .afterClosed().subscribe((confirm: boolean) => {
                if (confirm) {
                    this._videoService.delete(id).subscribe(
                        (data: void) => {
                            this.logger.log(data);
                            this.data = this.data.filter((video: IVideo) => {
                                return video.id !== id;
                            });
                            this.filteredData = this.filteredData.filter((video: IVideo) => {
                                return video.id !== id;
                            });
                            this._snackBarService.open('Video deleted', 'Ok', { duration: 3000 });
                        },
                        (error: Error) => {
                            this._dialogService.openAlert({ message: 'There was an error=' + error });
                        },
                    );
                }
            });
    }

    reload(): void {
        this.data = [];
        this.filter();
        this.loadVideos();
    }

    retranslateColumnHeaders(): void {
        this.columns.forEach(async (col: ITdDataTableColumn) => {
            if (col.label) {
                col.label = await this._translate.get(this.lookup[col.name]).toPromise();
            }
        });
        this.dataTable.refresh();
    }

    private filter(): void {
        let newData: any[] = this.data;
        newData = this._dataTableService.filterData(newData, this.searchTerm, true);
        this.filteredTotal = newData.length;
        newData = this._dataTableService.sortData(newData, this.sortBy, this.sortOrder);
        newData = this._dataTableService.pageData(newData, this.fromRow, this.currentPage * this.pageSize);
        this.filteredData = newData;
    }

    private loadVideos(): void {
        this._videoService.getAll().subscribe(
            (dataArray: IDataArray<IVideo>) => {
                this.data = dataArray.data;
            },
            (err: any) => {
                this.logger.error(err);
            },
            () => {
                this.logger.log('loaded videos', this.data);
                this.filter();
            },
        );
    }

}
